import { useState, useEffect, useCallback, useRef } from "react";
import {
  Select,
  InputNumber,
  Typography,
  Space,
  Button,
  Spin,
  Tag,
} from "antd";
import {
  HolderOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { ReactSortable } from "react-sortablejs";
import axios from "axios";
import API_URLS from "../../config/global_variables";

const authToken = () => {
  try {
    return JSON.parse(localStorage.userData || "{}").token || "";
  } catch {
    return "";
  }
};

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: authToken(),
});

/**
 * Fetch global tags from /api/v1/admin/tags.
 * Returns flat array of { id, tag }.
 */
const fetchGlobalTags = async (search = "") => {
  const params = { page: 1, limit: 100, sort_by: "tag", sort_order: "asc" };
  if (search) params.search = search;
  const res = await axios.get(API_URLS.TAGS, { headers: headers(), params });
  return (res?.data?.data?.items || []).map((t) => ({ id: t.id, tag: t.tag }));
};

/**
 * TagSelector — reusable searchable multi-select with drag-and-drop ordering.
 *
 * value shape (array):
 *   withPoints=false → [{ id, tag }]
 *   withPoints=true  → [{ id, tag, points }]
 *
 * The `id` here is the global tag id, NOT the keyword-points record id.
 * Callers are responsible for converting value ↔ their API payload format.
 */
const TagSelector = ({
  value = [],
  onChange,
  withPoints = false,
  disabled = false,
  placeholder = "Search and select tags…",
}) => {
  const [options, setOptions]       = useState([]);
  const [fetching, setFetching]     = useState(false);
  const [searchText, setSearchText] = useState("");
  const debounceRef = useRef(null);

  const loadTags = useCallback(async (search) => {
    setFetching(true);
    try {
      const tags = await fetchGlobalTags(search);
      setOptions(tags);
    } catch {
      setOptions([]);
    } finally {
      setFetching(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadTags("");
  }, [loadTags]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadTags(searchText);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchText, loadTags]);

  // Build sets of already-selected identifiers — support both {id,tag} and {keyword} shapes
  const selectedIds = new Set(
    value.map((v) => String(v.id ?? v.tag_id ?? "")).filter(Boolean)
  );
  const selectedNames = new Set(
    value.map((v) => String(v.tag ?? v.keyword ?? "").trim().toLowerCase()).filter(Boolean)
  );

  // Drop-down options excluding already-selected tags by id OR name
  const dropdownOptions = options
    .filter((o) => {
      const oId   = String(o.id ?? "");
      const oName = String(o.tag ?? "").trim().toLowerCase();
      return !selectedIds.has(oId) && !selectedNames.has(oName);
    })
    .map((o) => ({ value: o.id, label: o.tag }));

  const handleSelect = (tagId) => {
    const found = options.find((o) => o.id === tagId);
    if (!found) return;

    // Safety guard: reject if already present by id or by name (handles async race)
    const foundName = String(found.tag ?? "").trim().toLowerCase();
    const isDuplicate = value.some((v) => {
      const existingId   = String(v.id ?? v.tag_id ?? "");
      const existingName = String(v.tag ?? v.keyword ?? "").trim().toLowerCase();
      return existingId === String(found.id) || existingName === foundName;
    });
    if (isDuplicate) return;

    onChange?.([
      ...value,
      { id: found.id, tag: found.tag, ...(withPoints ? { points: 0 } : {}) },
    ]);
  };

  const handleRemove = (tagId) => {
    onChange?.(value.filter((v) => v.id !== tagId));
  };

  const handlePointsChange = (tagId, pts) => {
    onChange?.(value.map((v) => (v.id === tagId ? { ...v, points: pts ?? 0 } : v)));
  };

  // react-sortablejs expects items to have an `id` field — our items already do
  const handleSortEnd = (newOrder) => {
    onChange?.(newOrder);
  };

  return (
    <div>
      {/* Dropdown */}
      {!disabled && (
        <Select
          showSearch
          value={null}
          placeholder={placeholder}
          style={{ width: "100%", marginBottom: 10 }}
          filterOption={false}
          notFoundContent={
            fetching ? (
              <Spin size="small" />
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {searchText
                  ? "No tags found for your search."
                  : "No tags found. Please add tags from Tags Management."}
              </Typography.Text>
            )
          }
          onSearch={setSearchText}
          onSelect={handleSelect}
          options={dropdownOptions}
          loading={fetching}
          allowClear={false}
        />
      )}

      {/* Selected tag chips with drag-and-drop */}
      {value.length > 0 && (
        <ReactSortable
          list={value}
          setList={handleSortEnd}
          handle=".tag-drag-handle"
          animation={150}
          disabled={disabled}
          style={{ display: "flex", flexDirection: "column", gap: 6 }}
        >
          {value.map((item) => (
            <div
              key={item.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#f5f5f5",
                border: "1px solid #e0e0e0",
                borderRadius: 6,
                padding: "4px 8px",
                userSelect: "none",
              }}
            >
              {/* Drag handle */}
              {!disabled && (
                <span
                  className="tag-drag-handle"
                  style={{ cursor: "grab", color: "#bbb", flexShrink: 0, lineHeight: 1 }}
                >
                  <HolderOutlined />
                </span>
              )}

              {/* Tag name */}
              <Tag
                color="blue"
                style={{ margin: 0, flexShrink: 0, cursor: "default", fontSize: 13 }}
              >
                {item.tag}
              </Tag>

              {/* Points input */}
              {withPoints && (
                <Space size={4} style={{ flexShrink: 0 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Points
                  </Typography.Text>
                  <InputNumber
                    size="small"
                    min={0}
                    value={item.points ?? 0}
                    onChange={(v) => handlePointsChange(item.id, v)}
                    style={{ width: 65 }}
                    disabled={disabled}
                  />
                </Space>
              )}

              {/* Remove button */}
              {!disabled && (
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleRemove(item.id)}
                  style={{ marginLeft: "auto", flexShrink: 0, padding: "0 4px" }}
                />
              )}
            </div>
          ))}
        </ReactSortable>
      )}

      {value.length === 0 && disabled && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          No tags selected.
        </Typography.Text>
      )}
    </div>
  );
};

export default TagSelector;
